import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import { batches, records as recordsApi, type ImportRecord } from '../api/index.js';
import { Badge, statusBadge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import { Card, CardContent } from '../components/ui/card.js';
import {
  CheckCircle, XCircle, SkipForward, Play, RotateCcw,
  AlertTriangle, ChevronDown, ChevronUp, Loader2, Map, ClipboardList
} from 'lucide-react';
import { SearchableSelect } from '../components/SearchableSelect.js';

const RECORD_STATUS_FILTER_OPTS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'applied', label: 'Applied' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
];

const RECORD_ACTION_FILTER_OPTS = [
  { value: '', label: 'All actions' },
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'no_op', label: 'No-op' },
  { value: 'conflict', label: 'Conflict' },
  { value: 'blocked', label: 'Blocked' },
];

function DiffPanel({ record }: { record: ImportRecord }) {
  const [open, setOpen] = useState(false);
  const diff = record.diffJson;
  if (!diff || diff.fields.length === 0) return null;

  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
      >
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        {diff.fields.length} field changes {diff.hasHighRisk && '⚠ high-risk'}
      </button>
      {open && (
        <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden text-xs">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-gray-500">Field</th>
                <th className="px-3 py-2 text-left text-gray-500">Incoming</th>
                <th className="px-3 py-2 text-left text-gray-500">Existing</th>
                <th className="px-3 py-2 text-left text-gray-500">Risk</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {diff.fields.map((f) => (
                <tr key={f.field} className={f.risk === 'high_risk' ? 'bg-red-50' : ''}>
                  <td className="px-3 py-2 font-mono font-medium">{f.field}</td>
                  <td className="px-3 py-2 text-green-700 max-w-xs truncate">{JSON.stringify(f.incoming)}</td>
                  <td className="px-3 py-2 text-gray-500 max-w-xs truncate">{JSON.stringify(f.existing)}</td>
                  <td className="px-3 py-2">
                    <Badge variant={f.risk === 'high_risk' ? 'error' : f.risk === 'safe' ? 'success' : 'default'}>
                      {f.risk}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RecordRow({ record, onAction }: { record: ImportRecord; onAction: () => void }) {
  const canonical = record.canonicalJson as any;

  const approve = useMutation({
    mutationFn: () => recordsApi.approve(record.id),
    onSuccess: onAction,
  });
  const skip = useMutation({
    mutationFn: () => recordsApi.reject(record.id),
    onSuccess: onAction,
  });

  return (
    <div className="border border-gray-200 rounded-xl p-4 hover:border-brand-200 transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900 truncate">{canonical.name ?? '(no title)'}</span>
            {record.isbn13 && <span className="text-xs text-gray-400 font-mono">{record.isbn13}</span>}
            <Badge variant={statusBadge(record.diffAction ?? '')}>{record.diffAction ?? '—'}</Badge>
            <Badge variant={statusBadge(record.status)}>{record.status}</Badge>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {canonical.seriesName && <span>Series: <strong>{canonical.seriesName}</strong> · </span>}
            {canonical.publisherName && <span>Publisher: {canonical.publisherName}</span>}
          </p>
          {record.validationErrors && record.validationErrors.length > 0 && (
            <div className="mt-2 space-y-1">
              {record.validationErrors.map((e, i) => (
                <p key={i} className={`text-xs flex items-center gap-1 ${e.severity === 'error' ? 'text-red-600' : 'text-yellow-600'}`}>
                  <AlertTriangle size={10} /> {e.field}: {e.message}
                </p>
              ))}
            </div>
          )}
          {record.applyError && (
            <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
              <XCircle size={10} /> {record.applyError}
            </p>
          )}
          <DiffPanel record={record} />
        </div>
        {record.status === 'pending' && record.diffAction !== 'conflict' && (
          <div className="flex gap-2 flex-shrink-0">
            <Button size="sm" variant="success" onClick={() => approve.mutate()} loading={approve.isPending}>
              <CheckCircle size={14} /> Approve
            </Button>
            <Button size="sm" variant="ghost" onClick={() => skip.mutate()} loading={skip.isPending}>
              <SkipForward size={14} />
            </Button>
          </div>
        )}
        {record.status === 'pending' && record.diffAction === 'conflict' && (
          <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 flex-shrink-0">
            <AlertTriangle size={12} />
            Manual review required
          </div>
        )}
      </div>
    </div>
  );
}

export function BatchDetail() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [tab, setTab] = useState<'records' | 'audit'>('records');
  const [recordPage, setRecordPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');

  const { data: batch, isLoading: batchLoading, isError: batchError } = useQuery({
    queryKey: ['batch', id],
    queryFn: () => batches.get(id!),
    refetchInterval: 5_000,
  });

  const { data: recordData, isLoading: recordsLoading, isError: recordsError } = useQuery({
    queryKey: ['batch-records', id, recordPage, statusFilter, actionFilter],
    queryFn: () => batches.records(id!, recordPage, statusFilter || undefined, actionFilter || undefined),
    enabled: !!batch,
  });

  const { data: auditData, isLoading: auditLoading } = useQuery({
    queryKey: ['batch-audit', id],
    queryFn: () => batches.audit(id!),
    enabled: !!batch && tab === 'audit',
    staleTime: 30_000,
  });

  const approveAllPending = useMutation({
    mutationFn: async () => {
      const allIds: string[] = [];
      let p = 1;
      while (true) {
        const page = await batches.records(id!, p, 'pending');
        for (const r of page.records) {
          if (r.status === 'pending' && r.diffAction !== 'conflict') allIds.push(r.id);
        }
        if (p >= page.pages || page.records.length === 0) break;
        p++;
      }
      // Send in chunks of 100 to keep request size reasonable
      for (let i = 0; i < allIds.length; i += 100) {
        await recordsApi.bulkApprove(allIds.slice(i, i + 100), id!);
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['batch-records', id] }),
  });

  const apply = useMutation({
    mutationFn: () => batches.apply(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['batch', id] }),
  });

  const retry = useMutation({
    mutationFn: () => batches.retry(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['batch', id] }),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['batch', id] });
    qc.invalidateQueries({ queryKey: ['batch-records', id] });
  };

  if (batchLoading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <Loader2 className="animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (batchError || !batch) {
    return (
      <div className="p-8 max-w-5xl mx-auto">
        <Link to="/batches" className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-4">← Batches</Link>
        <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <AlertTriangle size={16} /> Failed to load batch. It may have been deleted or you may not have access.
        </div>
      </div>
    );
  }

  const canApply = batch.status === 'pending_review';
  const canRetry = batch.status === 'applied' || batch.status === 'failed';
  const needsMapping = batch.status === 'csv_pending_mapping';
  const totalPages = recordData ? recordData.pages : 0;

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <Link to="/batches" className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-4">← Batches</Link>

      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900 truncate">{batch.fileName}</h1>
            <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 uppercase font-mono">
              {(batch as any).source === 'csv' ? 'CSV' : 'ONIX'}
            </span>
          </div>
          <p className="text-gray-400 font-mono text-xs mt-1">{batch.id}</p>
          <p className="text-gray-500 text-sm mt-1">
            Uploaded {formatDistanceToNow(new Date(batch.createdAt), { addSuffix: true })}
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {needsMapping && (
            <Link to={`/batches/${id}/map`}>
              <Button variant="secondary" size="sm">
                <Map size={14} /> Map Columns
              </Button>
            </Link>
          )}
          {canRetry && (
            <Button variant="secondary" size="sm" onClick={() => retry.mutate()} loading={retry.isPending}>
              <RotateCcw size={14} /> Retry Failed
            </Button>
          )}
          {canApply && (
            <>
              <Button variant="secondary" size="sm" onClick={() => approveAllPending.mutate()} loading={approveAllPending.isPending}>
                <CheckCircle size={14} /> Approve All Safe
              </Button>
              <Button variant="success" size="sm" onClick={() => apply.mutate()} loading={apply.isPending}>
                <Play size={14} /> Apply to Admin
              </Button>
            </>
          )}
        </div>
      </div>

      {needsMapping && (
        <div className="mb-6 flex items-center gap-3 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg px-4 py-3 text-sm">
          <AlertTriangle size={16} className="shrink-0" />
          This CSV batch needs column mapping before it can be processed.
          <Link to={`/batches/${id}/map`} className="font-medium underline ml-1">Map columns →</Link>
        </div>
      )}

      {/* Stats bar */}
      <div className="grid grid-cols-5 gap-3 mb-8">
        {[
          { label: 'Total', value: batch.totalRecords, color: 'text-gray-900' },
          { label: 'Ready', value: batch.readyCount, color: 'text-green-700' },
          { label: 'Review', value: batch.reviewCount, color: 'text-yellow-700' },
          { label: 'Blocked', value: batch.blockedCount, color: 'text-red-700' },
          { label: 'No-op', value: batch.noOpCount, color: 'text-gray-500' },
        ].map(({ label, value, color }) => (
          <Card key={label}>
            <CardContent className="py-4 text-center">
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
              <p className="text-xs text-gray-500">{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mb-4 flex items-center gap-3">
        <Badge variant={statusBadge(batch.status)}>{batch.status.replace(/_/g, ' ')}</Badge>
        {batch.errorMessage && (
          <p className="text-sm text-red-600 flex items-center gap-1">
            <AlertTriangle size={14} /> {batch.errorMessage}
          </p>
        )}
      </div>

      {/* Tab nav */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {([['records', 'Records'], ['audit', 'Audit Trail']] as const).map(([t, label]) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              tab === t
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t === 'audit' ? <span className="flex items-center gap-1"><ClipboardList size={13} />{label}</span> : label}
          </button>
        ))}
      </div>

      {tab === 'audit' ? (
        <Card>
          {auditLoading ? (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <Loader2 className="animate-spin mr-2" /> Loading audit trail…
            </div>
          ) : !auditData?.length ? (
            <p className="text-center py-10 text-gray-400 text-sm">No audit events recorded for this batch.</p>
          ) : (
            <div className="divide-y divide-gray-50">
              {(auditData ?? []).map((entry) => (
                <div key={entry.id} className="px-5 py-3 flex items-start gap-4 text-sm">
                  <span className="text-xs text-gray-400 whitespace-nowrap w-36 shrink-0 pt-0.5">
                    {format(new Date(entry.createdAt), 'MMM d HH:mm:ss')}
                  </span>
                  <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-700 shrink-0">{entry.action}</span>
                  {entry.detail != null && (
                    <span className="text-gray-500 text-xs truncate">{JSON.stringify(entry.detail as object)}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      ) : (
      <>

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap items-center">
        <div className="min-w-[11rem] max-w-xs flex-1">
          <SearchableSelect
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              setRecordPage(1);
            }}
            options={RECORD_STATUS_FILTER_OPTS}
            placeholder="Status filter"
            emptyLabel="All statuses"
            allowClear={false}
          />
        </div>
        <div className="min-w-[11rem] max-w-xs flex-1">
          <SearchableSelect
            value={actionFilter}
            onChange={(v) => {
              setActionFilter(v);
              setRecordPage(1);
            }}
            options={RECORD_ACTION_FILTER_OPTS}
            placeholder="Action filter"
            emptyLabel="All actions"
            allowClear={false}
          />
        </div>
      </div>

      {recordsLoading ? (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <Loader2 className="animate-spin mr-2" /> Loading records…
        </div>
      ) : recordsError ? (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm">
          <AlertTriangle size={16} /> Failed to load records — try refreshing the page.
        </div>
      ) : (
        <div className="space-y-3">
          {recordData?.records.map((record) => (
            <RecordRow key={record.id} record={record} onAction={refresh} />
          ))}
          {recordData?.records.length === 0 && (
            <p className="text-center py-12 text-gray-400">No records match the selected filters</p>
          )}
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-gray-500">Page {recordPage} of {totalPages} · {recordData?.total} records</p>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" disabled={recordPage <= 1} onClick={() => setRecordPage(p => p - 1)}>Prev</Button>
            <Button variant="secondary" size="sm" disabled={recordPage >= totalPages} onClick={() => setRecordPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
